import React from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Box, Button, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { useResetPassword } from '../../hooks/use-reset-password.hook';
import PasswordCriteriaList from './PasswordCriteriaList.component';
import { validatePassword } from './password-rules.util';
import { clientAccentOverrides } from './authentication-form.styles';

interface ResetPasswordFormProps {
  token: string;
  email?: string;
  onSuccess: () => void;
}

interface ResetPasswordFormValues {
  password: string;
  passwordConfirm: string;
}

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    ...clientAccentOverrides(theme),
  },
  submit: {
    backgroundColor: theme.palette.client.primary.main,
    color: '#fff',
    minHeight: 48,
  },
}));

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({ token, email, onSuccess }) => {
  const classes = useStyles();
  const { isSubmitting, error, submit } = useResetPassword();

  const { control, handleSubmit, formState, watch } = useForm<ResetPasswordFormValues>({
    mode: 'onSubmit',
    defaultValues: { password: '', passwordConfirm: '' },
  });

  const password = watch('password');

  const onSubmit = handleSubmit(async (values) => {
    const isSuccess = await submit({ token, password: values.password, passwordConfirm: values.passwordConfirm });

    if (isSuccess) onSuccess();
  });

  return (
    <form className={classes.form} onSubmit={onSubmit} noValidate>
      <input type="email" name="username" autoComplete="username" value={email ?? ''} readOnly hidden />

      <Controller
        name="password"
        control={control}
        rules={{
          required: 'Veuillez saisir un mot de passe',
          validate: (value) => validatePassword(value),
        }}
        render={({ field }) => (
          <TextField
            {...field}
            inputRef={field.ref}
            autoFocus
            label="Nouveau mot de passe"
            type="password"
            autoComplete="new-password"
            variant="outlined"
            error={Boolean(formState.errors.password)}
            helperText={formState.errors.password?.message}
            inputProps={{ name: 'password', 'aria-invalid': Boolean(formState.errors.password) }}
          />
        )}
      />

      <PasswordCriteriaList password={password} />

      <Controller
        name="passwordConfirm"
        control={control}
        rules={{
          required: 'Veuillez confirmer le mot de passe',
          validate: (value) => value === password || 'Les mots de passe ne correspondent pas',
        }}
        render={({ field }) => (
          <TextField
            {...field}
            inputRef={field.ref}
            label="Confirmer le mot de passe"
            type="password"
            autoComplete="new-password"
            variant="outlined"
            error={Boolean(formState.errors.passwordConfirm)}
            helperText={formState.errors.passwordConfirm?.message}
            inputProps={{ name: 'passwordConfirm', 'aria-invalid': Boolean(formState.errors.passwordConfirm) }}
          />
        )}
      />

      <Box aria-live="polite">
        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}
      </Box>

      <Button className={classes.submit} type="submit" variant="contained" disabled={isSubmitting}>
        Réinitialiser le mot de passe
      </Button>
    </form>
  );
};

export default ResetPasswordForm;
